import React, { useEffect, useState, useCallback } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import { Link, useHistory, useParams } from "react-router-dom";

import Container from "../../../../../../components/Container";
import Layout from "../../../../../../components/Layout";
import CheckBox from "../../../../../../components/CheckBox";
import Content from "../../../../../../components/Content";
import Flex from "../../../../../../components/Flex";
import Label from "../../../../../../components/Label";
import TextBox from "../../../../../../components/TextBox";
import Button from "../../../../../../components/Button";
import ButtonList from "../../../../../../components/ButtonList";
import { useFetch } from "../../../../../../components/useFetch";
import Breadcrumbs from "../../../../../../components/Breadcrumbs";
import { useMessageToast } from "../../../../../../components/MessageProvider";
import { useAuthState } from "../../../../../../components/AuthProvider";
import Tooltip from "../../../../../../components/Tooltip";
import errorMessageHandler from "../../../../errorMessageHandler";
import Spinner from "../../../../../../components/Loader/Spinner";
import DropDownList from "../../../../../../components/DropDownList";

import styles from "./AddEditSource.module.scss";

const AddEditSource = () => {
  const moment = require("moment-timezone");
  const clientConfigApiDomain = process.env.REACT_APP_CLIENT_CONFIG_API_DOMAIN;
  const organizationApiUrl = `${clientConfigApiDomain}/organizations`;
  const clientsApiUrl = `${clientConfigApiDomain}/clients`;
  const sourcesApiUrl = `${clientConfigApiDomain}/sources`;
  const clientSubscriptionsApiUrl = `${clientConfigApiDomain}/client_subscriptions`;
  const clientSubscriptionsApiUrlNoauth = `${clientConfigApiDomain}/noauth/client_subscriptions`;
  const { id, clientId, sourceId } = useParams();

  const history = useHistory();
  const { user } = useAuthState();
  const toast = useMessageToast();

  const [
    { hasLoaded: addEditHasLoaded, hasError: addEditHasError, error },
    addEditFetch
  ] = useFetch();

  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [sourceData, setSourceData] = useState([]);
  const [clientData, setClientData] = useState([]);
  const [organizationData, setOrganizationData] = useState([]);
  const [subscriptionData, setSubscriptionData] = useState([]);

  const fetchInitialData = useCallback(async () => {
    window.scrollTo(0, 0);
    setIsLoading(true);
    if (sourceId !== undefined) {
      const response = await fetch(`${sourcesApiUrl}/${sourceId}/`);
      const data = await response.json();
      setSourceData(data);
    }

    const clientResponse = await fetch(`${clientsApiUrl}/${clientId}/`);
    const clientData = await clientResponse.json();
    setClientData(clientData);

    const organizationResponse = await fetch(`${organizationApiUrl}/${id}/`);
    const organizationData = await organizationResponse.json();
    setOrganizationData(organizationData);

    const subscriptionsResponse = await fetch(
      `${clientSubscriptionsApiUrl}/?client__id=${clientId}`
    );
    const subscriptionData = await subscriptionsResponse.json();
    setSubscriptionData(subscriptionData.results[0]);

    setIsLoading(false);
    setHasLoaded(true);
  }, [
    clientId,
    clientsApiUrl,
    id,
    organizationApiUrl,
    sourceId,
    sourcesApiUrl,
    clientSubscriptionsApiUrl
  ]);

  useEffect(() => {
    if (!hasLoaded) {
      fetchInitialData();
    } else {
      setHasLoaded(true);
    }
  }, [sourceId, fetchInitialData, hasLoaded]);

  useEffect(() => {
    if (sourceId === undefined && addEditHasLoaded) {
      if (addEditHasLoaded) {
        toast.success(`Source added successfully.`);

        history.push(`/admin/organizations/${id}/accounts/${clientId}`);
      } else if (addEditHasError) {
        if (error.response.status === 400) {
          const errorMsgArray = errorMessageHandler(error.response.data);
          errorMsgArray.forEach(msg => toast.error(msg));
        } else toast.error(`Failed to add source.`);
      }
    } else {
      if (addEditHasLoaded) {
        toast.success(`Source updated successfully.`);

        history.push(
          `/admin/organizations/${id}/accounts/${clientId}/sources/${sourceId}`
        );
      } else if (addEditHasError) {
        if (error.response.status === 400) {
          const errorMsgArray = errorMessageHandler(error.response.data);
          errorMsgArray.forEach(msg => toast.error(msg));
        } else toast.error(`Failed to update this source.`);
      }
    }
  }, [
    sourceId,
    addEditHasError,
    addEditHasLoaded,
    toast,
    history,
    id,
    clientId,
    error
  ]);

  let loadedBreadCrumbs = "";
  if (isLoading) {
    loadedBreadCrumbs = <Spinner />;
  } else if (hasLoaded) {
    loadedBreadCrumbs = (
      <Breadcrumbs>
        <Breadcrumbs.Item render={() => <Link to="/">Home</Link>} />
        <Breadcrumbs.Item
          render={() => <Link to="/admin">Administration</Link>}
        />
        <Breadcrumbs.Item
          render={() => <Link to="/admin/organizations">Organizations</Link>}
        />
        <Breadcrumbs.Item
          render={() => (
            <Link to={`/admin/organizations/${id}`}>
              Organization: {organizationData.name}
            </Link>
          )}
        />
        <Breadcrumbs.Item
          render={() => (
            <Link to={`/admin/organizations/${id}/accounts/${clientId}`}>
              Account: {clientData.name}
            </Link>
          )}
        />
        {sourceId ? (
          <>
            <Breadcrumbs.Item
              render={() => (
                <Link
                  to={`/admin/organizations/${id}/accounts/${clientId}/sources/${sourceId}`}
                >
                  Source: {sourceData.name}
                </Link>
              )}
            />
            <Breadcrumbs.Item last render={() => "Edit"}></Breadcrumbs.Item>
          </>
        ) : (
          <Breadcrumbs.Item last render={() => "Add Source"}></Breadcrumbs.Item>
        )}
      </Breadcrumbs>
    );
  }

  return (
    <Layout>
      <Container>
        <Flex gutter={3}>
          <Flex.Item span={12}>{loadedBreadCrumbs}</Flex.Item>
          {isLoading ? (
            <Spinner />
          ) : hasLoaded ? (
            <Flex.Item span={12}>
              <Content>
                <h1>
                  {sourceId ? `Edit Source: ${sourceData.name}` : `Add Source`}
                </h1>
              </Content>
              <div className={styles.base}>
                <div className={styles.detailsFlex}>
                  <h3>Source Details</h3>
                </div>
                <div className={styles.details}>
                  <Formik
                    initialValues={{
                      sourceName: sourceData.name,
                      oid: sourceData.oid ? sourceData.oid : null,
                      code: sourceData.code ? sourceData.code : null,
                      defaultTrustScore: sourceData.trust_score,
                      assigningAuthorityOid: sourceData.assigning_authority_oid,
                      subscription: `${process.env.REACT_APP_DOMAIN}${clientSubscriptionsApiUrlNoauth}/${subscriptionData.id}`,
                      trustedMasterIdentifier:
                        sourceData.length === 0
                          ? true
                          : sourceData.trusted_master_identifier,
                      restriction: sourceData.restriction,
                      createdBy: sourceData.created_by,
                      updatedBy: sourceData.updated_by
                    }}
                    enableReinitialize={true}
                    onSubmit={(values, { setSubmitting, resetForm }) => {
                      setSubmitting(true);
                      let data = {
                        name: values.sourceName,
                        oid: values.oid == "" ? null : values.oid,
                        code: values.code == "" ? null : values.code,
                        trust_score: values.defaultTrustScore,
                        assigning_authority_oid: values.assigningAuthorityOid,
                        trusted_master_identifier:
                          values.trustedMasterIdentifier,
                        restriction: values.restriction,
                        updated_by: user.username,
                        created_by: values.createdBy,
                        client_subscription: values.subscription
                      };
                      let method = "";
                      let url = "";

                      if (sourceId) {
                        data["id"] = sourceId;
                        method = "put";
                        url = `${sourcesApiUrl}/${sourceId}/`;
                      } else {
                        data["created_by"] = user.username;
                        method = "post";
                        url = `${sourcesApiUrl}/`;
                      }

                      addEditFetch({
                        method,
                        url,
                        data
                      });

                      setSubmitting(false);
                    }}
                    validationSchema={Yup.object().shape({
                      sourceName: Yup.string()
                        .trim()
                        .required("This field is required"),
                      oid: Yup.string()
                        .nullable()
                        .trim()
                        .matches(
                          /^([0-2])((\.0)|(\.[1-9][0-9]*))*$/,
                          "Invalid OID format"
                        ),
                      code: Yup.string()
                        .nullable()
                        .trim()
                        .when("oid", {
                          is: val => val == null,
                          then: Yup.string().required(
                            "Either oid or code is required."
                          )
                        }),
                      assigningAuthorityOid: Yup.string()
                        .trim()
                        .required("This field is required")
                        .matches(
                          /^([0-2])((\.0)|(\.[1-9][0-9]*))*$/,
                          "Invalid OID format"
                        ),
                      defaultTrustScore: Yup.string()
                        .trim()
                        .required("This field is required"),
                      trustedMasterIdentifier: Yup.boolean().required(
                        "Trusted Master Identifier is required."
                      )
                    })}
                  >
                    {props => {
                      const {
                        values,
                        touched,
                        errors,
                        handleChange,
                        handleBlur,
                        handleSubmit
                      } = props;

                      return (
                        <form onSubmit={handleSubmit} autoComplete="off">
                          <div>
                            {sourceId ? (
                              <>
                                <h4>Source ID</h4>
                                <span>{sourceData.id}</span>
                              </>
                            ) : null}
                          </div>
                          <div>
                            <h4>Subscription</h4>
                            <p>{subscriptionData.subscription.name}</p>
                          </div>
                          <div>
                            <TextBox
                              errorText={
                                touched.sourceName ? errors.sourceName : null
                              }
                              id="sourceName"
                              label={
                                <Label
                                  error={
                                    touched.sourceName && errors.sourceName
                                  }
                                  htmlFor="sourceName"
                                >
                                  <b>Source Name</b>
                                </Label>
                              }
                              onChange={handleChange}
                              onBlur={handleBlur}
                              required
                              value={values.sourceName}
                              width="25%"
                            />
                          </div>
                          <div>
                            <TextBox
                              errorText={touched.oid ? errors.oid : null}
                              id="oid"
                              label={
                                <Label
                                  error={touched.oid && errors.oid}
                                  htmlFor="oid"
                                >
                                  <b>Source OID</b>{" "}
                                  <Tooltip>
                                    <Content>
                                      <h3>OID format:</h3>
                                      <ul>
                                        <li>Should Start with 0, 1 or 2.</li>
                                        <li>
                                          Subsequent numbers should be prefixed
                                          with `.` and should not contain
                                          leading 0s.
                                        </li>
                                      </ul>
                                      <p style={{ paddingTop: "2rem" }}>
                                        Example: 2.0.11.256
                                      </p>
                                    </Content>
                                  </Tooltip>
                                </Label>
                              }
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.oid}
                              width="25%"
                            />
                          </div>
                          <div>
                            <TextBox
                              errorText={touched.code ? errors.code : null}
                              id="code"
                              label={
                                <Label
                                  error={touched.code && errors.code}
                                  htmlFor="code"
                                >
                                  <b>Source Code</b>{" "}
                                  <Tooltip>
                                    <Content>
                                      <p>
                                        If source OID is not provided then
                                        source code is mandatory
                                      </p>
                                    </Content>
                                  </Tooltip>
                                </Label>
                              }
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.code}
                              width="25%"
                            />
                          </div>
                          <div>
                            <TextBox
                              errorText={
                                touched.assigningAuthorityOid
                                  ? errors.assigningAuthorityOid
                                  : null
                              }
                              id="assigningAuthorityOid"
                              label={
                                <Label
                                  error={
                                    touched.assigningAuthorityOid &&
                                    errors.assigningAuthorityOid
                                  }
                                  htmlFor="assigningAuthorityOid"
                                >
                                  <b>Assigning Authority OID</b>{" "}
                                  <Tooltip>
                                    <Content>
                                      <h3>OID format:</h3>
                                      <ul>
                                        <li>Should Start with 0, 1 or 2.</li>
                                        <li>
                                          Subsequent numbers should be prefixed
                                          with `.` and should not contain
                                          leading 0s.
                                        </li>
                                      </ul>
                                      <p style={{ paddingTop: "2rem" }}>
                                        Example: 2.0.11.256
                                      </p>
                                    </Content>
                                  </Tooltip>
                                </Label>
                              }
                              onChange={handleChange}
                              onBlur={handleBlur}
                              required
                              value={values.assigningAuthorityOid}
                              width="25%"
                            />
                          </div>
                          <div>
                            <TextBox
                              errorText={
                                touched.defaultTrustScore
                                  ? errors.defaultTrustScore
                                  : null
                              }
                              id="defaultTrustScore"
                              label={
                                <Label
                                  error={
                                    touched.defaultTrustScore &&
                                    errors.defaultTrustScore
                                  }
                                  htmlFor="defaultTrustScore"
                                >
                                  <b>Default Trust Score</b>
                                </Label>
                              }
                              onChange={handleChange}
                              onBlur={handleBlur}
                              required
                              value={values.defaultTrustScore}
                              width="25%"
                            />
                          </div>
                          <div>
                            <h4>
                              Trusted Master Identifier{" "}
                              <Tooltip>
                                <Content>
                                  <p>
                                    When enabled, same MRN results in a record
                                    update. When disabled, matching score
                                    determines a record update.
                                  </p>
                                </Content>
                              </Tooltip>
                            </h4>
                            <div>
                              <CheckBox
                                id="trustedMasterIdentifier"
                                label={
                                  values.trustedMasterIdentifier
                                    ? "Enabled"
                                    : "Disabled"
                                }
                                onChange={handleChange}
                                selected={values.trustedMasterIdentifier}
                              />
                            </div>
                          </div>
                          <div>
                            <h4>
                              Restriction Type
                              <Tooltip>
                                <Content>
                                  <p>
                                    Restriction type determines how the source
                                    is restricted.
                                  </p>
                                </Content>
                              </Tooltip>
                            </h4>
                            <DropDownList
                              id="restriction"
                              defaultValue={values.restriction}
                              onChange={handleChange}
                            >
                              <DropDownList.Item value="sourceRestricted">
                                Source Restricted
                              </DropDownList.Item>
                              <DropDownList.Item value="policyRestricted">
                                Policy Restricted
                              </DropDownList.Item>
                              <DropDownList.Item value="noRestriction">
                                No Restriction
                              </DropDownList.Item>
                            </DropDownList>
                          </div>
                          {sourceId ? (
                            <>
                              <div>
                                <h4>Created By</h4>
                                <span>{subscriptionData.created_by}</span>
                              </div>
                              <div>
                                <h4>Date Created</h4>
                                <span>
                                  {moment(subscriptionData.created_at)
                                    .tz(user.preferences.timeZone)
                                    .format("L, LTS z")}
                                </span>
                              </div>
                              <div>
                                <h4>Edited By</h4>
                                <span>{subscriptionData.updated_by}</span>
                              </div>
                              <div>
                                <h4>Date Updated</h4>
                                <span>
                                  {moment(subscriptionData.updated_at)
                                    .tz(user.preferences.timeZone)
                                    .format("L, LTS z")}
                                </span>
                              </div>
                            </>
                          ) : null}
                          <div>
                            <ButtonList>
                              <Button
                                primary
                                type="submit"
                                disabled={
                                  user.role === "Global Admin" ||
                                  user.orgIds.includes(organizationData.id)
                                    ? false
                                    : true
                                }
                              >
                                {sourceId ? `Save Changes` : `Save`}
                              </Button>
                              <Button
                                onClick={() => {
                                  history.goBack();
                                }}
                              >
                                Cancel
                              </Button>
                            </ButtonList>
                          </div>
                        </form>
                      );
                    }}
                  </Formik>
                </div>
              </div>
            </Flex.Item>
          ) : null}
        </Flex>
      </Container>
    </Layout>
  );
};

export default AddEditSource;
